/**
 * Manager+ affordance gate for the pipeline surface flip (SE-6071).
 *
 * Returns `true` only when the authenticated user's
 * `organization_user.role.name` is Owner, Admin, or Manager — the
 * "Manager+" tier the epic (decision P3) grants pipeline access. Every
 * other state — MEMBER/Scout (Tier 3), a missing role, unauthenticated,
 * or the `undefined` flash while `/auth/me` is in flight — returns
 * `false`.
 *
 * The authoritative access control is the server-side gate: the migrated
 * PIPELINE-type board queries return not-found/empty for sub-Managers, so
 * a Member never sees pipeline data regardless of this hook. This hook is
 * the second, client layer that keeps the surface from resolving
 * `KanbanSourceType.Pipeline` (and thus rendering the pipeline chrome) for
 * a user who has no pipeline access — defense-in-depth / affordance-hiding,
 * mirroring `useIsContractOwner`.
 *
 * Treating the loading state as "not Manager+" fails closed: it matches the
 * backend service gate (which rejects until the `organization_user` junction
 * row is fetched) and prevents a one-frame flash of the pipeline surface
 * during the login hydration window.
 * @module features/player-kanban/hooks/useIsPipelineManager
 */

import { useCurrentUser } from "@/features/auth";

import { MANAGER_PLUS_ROLES } from "../constants/pipelineTierRoles";

/**
 * Returns whether the current user is Manager+ and may see the pipeline
 * surface.
 * @returns `true` only when `currentUser.role.name` is Owner, Admin, or
 * Manager. Returns `false` for MEMBER/Scout, missing role, unauthenticated,
 * and the `undefined` loading state (fail-closed).
 * @remarks Uses the generated `RoleName` enum values rather than string
 * literals to stay in sync with codegen changes.
 */
export function useIsPipelineManager(): boolean {
  const currentUser = useCurrentUser();
  const roleName = currentUser?.role?.name;
  return roleName !== undefined && MANAGER_PLUS_ROLES.has(roleName);
}
